import React, { useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import {
  ClearOutlined,
  CloseOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Category } from '../models/category.model';

export type ActiveFilter = {
  id: string;
  label: string;
  type: 'search' | 'category' | 'subcategory';
};

const SEARCH_PLACEHOLDER_TIPS = [
  'Search by job title, skill, or company…',
  'Try: Senior Java Developer',
  'Try: React, Python, or DevOps',
  'Try: remote, hybrid, or QA automation',
  'Type 3+ characters, then narrow by category',
];

const SEARCH_EXAMPLES = ['Senior Java', 'React', 'remote QA', 'AWS'];

interface FilterPanelProps {
  searchTerm: string;
  /** Full category catalog for the dropdown (scales to dozens of entries). */
  categories: Category[];
  selectedCategories: string[];
  activeFilters: ActiveFilter[];
  isFiltering?: boolean;
  disabled?: boolean;
  onSearchChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onCategorySelect: (categoryNames: string[]) => void;
  onRemoveFilter: (filter: ActiveFilter) => void;
  onClearAll: () => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  searchTerm,
  categories,
  selectedCategories,
  activeFilters,
  isFiltering = false,
  disabled = false,
  onSearchChange,
  onCategorySelect,
  onRemoveFilter,
  onClearAll,
}) => {
  const [placeholderTipIndex, setPlaceholderTipIndex] = useState(0);
  const showSearchHint = searchTerm.length > 0 && searchTerm.length < 3;
  const showSearchTips = searchTerm.length === 0;

  useEffect(() => {
    if (!showSearchTips) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setPlaceholderTipIndex(
        (current) => (current + 1) % SEARCH_PLACEHOLDER_TIPS.length
      );
    }, 4500);

    return () => window.clearInterval(intervalId);
  }, [showSearchTips]);

  const searchDescriptionId = showSearchHint
    ? 'search-hint'
    : showSearchTips
      ? 'search-tips'
      : undefined;

  const categoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        label: category.name,
        value: category.name,
      })),
    [categories]
  );

  return (
    <div
      className={`job-filter-panel mt-6${isFiltering ? ' is-filtering' : ''}`}
    >
      {/* ── Search row ───────────────────────────────────────── */}
      <div className="job-filter-search-row">
        <label className="job-filter-search">
          <span className="sr-only">Search jobs</span>
          <SearchOutlined
            className={`job-filter-search-icon${
              isFiltering ? ' job-filter-search-icon--loading' : ''
            }`}
            aria-hidden="true"
          />
          <input
            type="search"
            className="ats-input job-filter-search-input"
            placeholder={SEARCH_PLACEHOLDER_TIPS[placeholderTipIndex]}
            value={searchTerm}
            onChange={onSearchChange}
            aria-label="Search jobs by title, skill, or company"
            aria-describedby={searchDescriptionId}
            aria-busy={isFiltering}
          />
        </label>

        {activeFilters.length > 0 && (
          <button
            type="button"
            className="job-filter-clear-all"
            onClick={onClearAll}
            disabled={disabled}
          >
            <ClearOutlined />
            Clear all
          </button>
        )}
      </div>

      {showSearchHint && (
        <p id="search-hint" className="job-filter-hint">
          Enter at least 3 characters to search
        </p>
      )}

      {showSearchTips && (
        <p id="search-tips" className="job-filter-search-tips">
          <span className="job-filter-search-tips-label">Tips</span>
          <span className="job-filter-search-tips-text">
            Use titles, skills, or company names · at least 3 characters
          </span>
          <span className="job-filter-search-examples" aria-hidden="true">
            {SEARCH_EXAMPLES.map((example) => (
              <span key={example} className="job-filter-search-example">
                {example}
              </span>
            ))}
          </span>
        </p>
      )}

      {/* ── Category multi-select ────────────────────────────── */}
      <div className="job-filter-group">
        <label className="job-filter-label" htmlFor="category-filter-select">
          Categories
        </label>
        <Select
          id="category-filter-select"
          mode="multiple"
          className="job-filter-category-select"
          popupClassName="job-filter-category-dropdown"
          showSearch
          allowClear
          disabled={disabled}
          value={selectedCategories}
          placeholder="All categories"
          optionFilterProp="label"
          options={categoryOptions}
          maxTagCount="responsive"
          aria-label="Filter by categories"
          notFoundContent="No categories match"
          onChange={(values: string[]) => onCategorySelect(values)}
          onClear={() => onCategorySelect([])}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
        />
      </div>

      {/* ── Active filter chips ──────────────────────────────── */}
      {activeFilters.length > 0 && (
        <div className="job-active-filters">
          <span className="job-filter-label">Active filters</span>
          <div className="job-active-filters-list">
            {activeFilters.map((activeFilter) => (
              <button
                key={activeFilter.id}
                type="button"
                className="job-active-filter-chip"
                onClick={() => onRemoveFilter(activeFilter)}
                disabled={disabled}
                aria-label={`Remove ${activeFilter.label} filter`}
              >
                <span>{activeFilter.label}</span>
                <CloseOutlined aria-hidden="true" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterPanel;
